import type { PresenceStatus, UserDto } from '@lagerfeuer/shared';
import { useState } from 'react';
import { useAppStore } from '../store/app';

interface Props {
  user: Pick<UserDto, 'id' | 'username' | 'displayName' | 'avatarUrl' | 'avatarAnimated'> | null | undefined;
  size?: number;
  /** Server-Avatar (Mitglied) überschreibt den Nutzer-Avatar. */
  overrideUrl?: string | null;
  /** Präsenz-Punkt anzeigen (aus dem Store). */
  showStatus?: boolean;
  /** Animierte Avatare nur bei Hover/Fokus abspielen. */
  animate?: boolean;
  className?: string;
}

/** Animierte GIF-Avatare: statisches Bild über `?static=1` ist im Vertrag nicht vorgesehen – daher Abspielen gemäß Einstellung. */
export function Avatar({ user, size = 40, overrideUrl, showStatus, animate, className }: Props) {
  const [broken, setBroken] = useState(false);
  const status = useAppStore((s) => (showStatus && user ? effectiveStatus(s.presences[user.id]?.status) : null));
  const autoplay = useAppStore((s) => s.settings?.autoplayGifs ?? true);
  const url = overrideUrl ?? user?.avatarUrl ?? null;
  const name = user?.displayName || user?.username || '?';
  const paused = user?.avatarAnimated && !autoplay && !animate;
  return (
    <span className={`avatar ${className ?? ''}`} style={{ width: size, height: size }} data-testid="avatar">
      {url && !broken && !paused ? (
        <img src={url} alt="" loading="lazy" onError={() => setBroken(true)} draggable={false} />
      ) : (
        <span className="avatar-fallback" style={{ fontSize: size * 0.42 }} aria-hidden="true">
          {initials(name)}
        </span>
      )}
      {status && <span className="status-dot" data-status={status} data-testid="status-dot" aria-label={STATUS_LABELS[status]} role="img" />}
    </span>
  );
}

export function initials(name: string): string {
  const parts = name.trim().split(/[\s_.-]+/).filter(Boolean);
  const s = parts.length > 1 ? `${parts[0]![0]}${parts[1]![0]}` : name.slice(0, 2);
  return s.toUpperCase();
}

/** INVISIBLE erscheint anderen als OFFLINE; fehlende Präsenz = OFFLINE. */
export function effectiveStatus(s: PresenceStatus | undefined): PresenceStatus {
  if (!s || s === 'INVISIBLE') return 'OFFLINE';
  return s;
}

export const STATUS_LABELS: Record<PresenceStatus, string> = {
  ONLINE: 'Online',
  IDLE: 'Abwesend',
  DND: 'Bitte nicht stören',
  INVISIBLE: 'Unsichtbar',
  OFFLINE: 'Offline',
};
